import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { ErrorNotice } from '@/components/common/ErrorNotice';
import { MEMBERSHIP_TYPES } from '@/constants/users';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useAuth } from '@/hooks/useAuth';
import { useSuiteLoungeFlow } from '@/hooks/useSuiteLoungeFlow';

interface SignUpVerificationCodeFormProps {
  email: string;
  password: string;
  signupSessionId: string;
  firstName: string;
  lastName: string;
  onVerified: () => void;
  onSignupSessionIdUpdate?: (newSessionId: string) => void;
}

export const SignUpVerificationCodeForm = ({
  email,
  password,
  signupSessionId,
  firstName,
  lastName,
  onVerified,
  onSignupSessionIdUpdate,
}: SignUpVerificationCodeFormProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { isSuiteLoungeFlow } = useSuiteLoungeFlow();
  const { translateApiError } = useApiErrorTranslation();
  const [code, setCode] = useState<string[]>(['', '', '', '']);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { isRequesting, execute } = useApiRequest(
    'POST',
    'UserRegistrationComplete',
  );

  const { isRequesting: isResending, execute: executeResend } = useApiRequest(
    'POST',
    'UserRegistration',
  );

  const handleChange = (index: number, value: string) => {
    // 数字のみ許可
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // 入力されたら次のフィールドにフォーカス
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Backspaceで前のフィールドに戻る
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 4);
    const newCode = [...code];

    for (let i = 0; i < pastedData.length && i < 4; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newCode[i] = pastedData[i];
      }
    }

    setCode(newCode);

    // ペースト後、最後の入力フィールドにフォーカス
    const lastFilledIndex = Math.min(pastedData.length, 3);
    inputRefs.current[lastFilledIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const verificationCode = code.join('');
    if (verificationCode.length === 4) {
      setErrorMessage('');
      setErrorDetails([]);
      setSuccessMessage('');

      const result = await execute({
        email,
        authorization_code: verificationCode,
        password,
        signup_session_id: signupSessionId,
        membership_type: isSuiteLoungeFlow
          ? MEMBERSHIP_TYPES.SUITE_LOUNGE
          : MEMBERSHIP_TYPES.ANA_LOUNGE,
      });

      if (result.ok) {
        // JWTトークンを保存（loginメソッドを使用）
        auth.login(
          result.data.data.jwt,
          result.data.data.expires_at,
          result.data.data.role,
        );

        // 親コンポーネントに通知（完了画面へ遷移）
        onVerified();
      } else {
        const { message, detailMessages } = translateApiError(
          result.error,
          result.details,
        );
        setErrorMessage(message);
        setErrorDetails(detailMessages);
      }
    }
  };

  const handleResend = async () => {
    setErrorMessage('');
    setErrorDetails([]);
    setSuccessMessage('');
    const result = await executeResend({
      email,
      first_name_jp: firstName,
      family_name_jp: lastName,
      membership_type: isSuiteLoungeFlow
        ? MEMBERSHIP_TYPES.SUITE_LOUNGE
        : MEMBERSHIP_TYPES.ANA_LOUNGE,
    });

    if (result.ok) {
      setSuccessMessage(t('signupVerify.resendSuccess'));
      // 新しいsignup_session_idを親コンポーネントに通知
      if (
        onSignupSessionIdUpdate &&
        'signup_session_id' in result.data.data &&
        result.data.data.signup_session_id
      ) {
        onSignupSessionIdUpdate(result.data.data.signup_session_id);
      }
    } else {
      const { message, detailMessages } = translateApiError(
        result.error,
        result.details,
      );
      setErrorMessage(message);
      setErrorDetails(detailMessages);
    }
  };

  const isCodeComplete = code.every((digit) => digit !== '');

  return (
    <form className="c-auth__form" onSubmit={handleSubmit}>
      {/* メール送信完了メッセージ */}
      <div className="password-mail">
        <div className="password-mail__icon">
          <img
            src="/Common_Icon_Mail.png"
            alt={t('common.altText.attention')}
            className="password-mail__icon-img"
          />
        </div>

        <div className="password-mail__message">
          {t('signupVerify.message')}
        </div>

        <div className="password-mail__subtext">
          {t('signupVerify.subtext')}
        </div>

        <div className="password-mail__code">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              className="password-mail__code-input"
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={1}
              name={`code${index + 1}`}
              autoComplete="one-time-code"
              required
              value={digit}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(index, e.target.value)
              }
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(index, e)
              }
              onPaste={index === 0 ? handlePaste : undefined}
            />
          ))}
        </div>
      </div>

      {/* エラーメッセージ */}
      {errorMessage && (
        <ErrorNotice message={errorMessage} details={errorDetails} />
      )}

      {/* 成功メッセージ */}
      {successMessage && <p className="common-success">{successMessage}</p>}

      {/* TODO: resend時にinitial_login_patternが返ってくることを考慮する */}
      {/* アクションボックス */}
      <div className="actions">
        <button
          className={`password-change__submit c-auth__btn c-auth__btn--register ${!isCodeComplete || isRequesting ? 'is-disabled' : ''}`}
          type="submit"
          disabled={!isCodeComplete || isRequesting}
        >
          {isRequesting ? (
            <span>
              {t('signupVerify.registerButtonLoading')}
              <br />
              {t('signupVerify.registerButtonLoadingDescription')}
            </span>
          ) : (
            t('signupVerify.registerButton')
          )}
        </button>
        <button
          className="password-mail__resend"
          type="button"
          onClick={handleResend}
          disabled={isResending || isRequesting}
        >
          {isResending
            ? t('signupVerify.resendButtonLoading')
            : t('signupVerify.resendButton')}
        </button>
      </div>

      {/* 注意ボックス */}
      <div className="l-notice-box l-mb30">
        <div className="l-notice-box__inner">
          <div className="l-notice-box__icon">
            <img
              src="/Common_Icon_Note.png"
              alt={t('common.altText.attention')}
              className="l-notice-box__icon-img"
            />
          </div>
          <div className="l-notice-box__text">
            {t('signupVerify.emailNotReceived')}
            <br />
            {t('signupVerify.checkFollowing')}
          </div>
        </div>
        <div className="l-notice-box__list">
          <div className="l-notice-box__item">
            <div className="l-notice-box__item-marker">・</div>
            <div className="l-notice-box__item-text">
              {t('signupVerify.spamFolder')}
            </div>
          </div>
          <div className="l-notice-box__item">
            <div className="l-notice-box__item-marker">・</div>
            <div className="l-notice-box__item-text">
              {t('signupVerify.incorrectEmail')}
            </div>
          </div>
          <div className="l-notice-box__item l-notice-box__item--note">
            <div className="l-notice-box__item-marker">※</div>
            <div className="l-notice-box__item-text">
              {t('signupVerify.urlValidity')}
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};
